import React, { useState, useEffect } from 'react';
import { ProjectArtifact } from '../../types';
import { apiService } from '../../services/api';
import { CreateArtifactModal } from './CreateArtifactModal';
import { EditArtifactModal } from './EditArtifactModal';
import { 
  DocumentIcon, 
  DocumentTextIcon, 
  FolderIcon, 
  ClipboardDocumentListIcon,
  ClipboardDocumentIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

interface ProjectArtifactsListProps {
  projectId: string;
}

const ARTIFACT_TYPE_ICONS = {
  document: DocumentTextIcon,
  template: DocumentIcon,
  resource: FolderIcon,
  deliverable: ClipboardDocumentIcon,
  checklist: ClipboardDocumentListIcon
} as const;

const ARTIFACT_TYPE_LABELS = {
  document: 'Dokument',
  template: 'Vorlage',
  resource: 'Ressource',
  deliverable: 'Lieferobjekt',
  checklist: 'Checkliste'
} as const;

export const ProjectArtifactsList: React.FC<ProjectArtifactsListProps> = ({ projectId }) => {
  const [artifacts, setArtifacts] = useState<ProjectArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState<ProjectArtifact | null>(null);

  useEffect(() => {
    fetchArtifacts();
  }, [projectId]);

  const fetchArtifacts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectArtifacts(projectId);
      setArtifacts(response.artifacts);
      setError(null);
    } catch (error) {
      console.error('Error fetching artifacts:', error);
      setError('Fehler beim Laden der Projekt-Artefakte');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArtifact = async (artifactData: {
    artifactType: string;
    name: string;
    description?: string;
    isRequired?: boolean;
    data?: any;
  }) => {
    try {
      await apiService.createProjectArtifact({
        projectId,
        ...artifactData
      });
      await fetchArtifacts();
      setCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating artifact:', error);
      setError('Fehler beim Erstellen des Artefakts');
    }
  };

  const handleUpdateArtifact = async (artifactId: string, updateData: {
    artifactType?: string;
    name?: string;
    description?: string;
    isRequired?: boolean;
    data?: any;
  }) => {
    try {
      await apiService.updateProjectArtifact(artifactId, updateData);
      await fetchArtifacts();
      setEditModalOpen(false);
      setSelectedArtifact(null);
    } catch (error) {
      console.error('Error updating artifact:', error);
      setError('Fehler beim Aktualisieren des Artefakts');
    }
  };

  const handleDeleteArtifact = async (artifactId: string) => {
    if (!window.confirm('Sind Sie sicher, dass Sie dieses Artefakt löschen möchten?')) {
      return;
    }

    try {
      await apiService.deleteProjectArtifact(artifactId);
      await fetchArtifacts();
    } catch (error) {
      console.error('Error deleting artifact:', error);
      setError('Fehler beim Löschen des Artefakts');
    }
  };

  const handleEditArtifact = (artifact: ProjectArtifact) => {
    setSelectedArtifact(artifact);
    setEditModalOpen(true);
  };

  const groupedArtifacts = artifacts.reduce((groups, artifact) => {
    const type = artifact.artifactType;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(artifact);
    return groups;
  }, {} as Record<string, ProjectArtifact[]>);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Projekt-Artefakte</h3>
          <p className="text-sm text-gray-600">
            Vorlagen, Dokumente und Ressourcen für dieses Projekt
          </p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Artefakt hinzufügen
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {artifacts.length === 0 ? (
        <div className="text-center py-8">
          <DocumentIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Keine Artefakte vorhanden</h4>
          <p className="text-gray-600 mb-4">
            Fügen Sie Vorlagen, Dokumente und Ressourcen für Ihr Projekt hinzu.
          </p>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Erstes Artefakt hinzufügen
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedArtifacts).map(([type, typeArtifacts]) => {
            const IconComponent = ARTIFACT_TYPE_ICONS[type as keyof typeof ARTIFACT_TYPE_ICONS] || DocumentIcon;
            const typeLabel = ARTIFACT_TYPE_LABELS[type as keyof typeof ARTIFACT_TYPE_LABELS] || type;
            
            return (
              <div key={type} className="border rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <IconComponent className="w-5 h-5 text-gray-500 mr-2" />
                  <h4 className="font-medium text-gray-900">{typeLabel}</h4>
                  <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                    {typeArtifacts.length}
                  </span>
                </div>
                
                <div className="space-y-3">
                  {typeArtifacts.map((artifact) => (
                    <div
                      key={artifact.id}
                      className="flex items-start justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <h5 className="font-medium text-gray-900">{artifact.name}</h5>
                          {artifact.isRequired && (
                            <span className="ml-2 bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                              Erforderlich
                            </span>
                          )}
                        </div>
                        {artifact.description && (
                          <p className="text-sm text-gray-600 mb-2">{artifact.description}</p>
                        )}
                        {artifact.data && Object.keys(artifact.data).length > 0 && (
                          <div className="text-xs text-gray-500">
                            {JSON.stringify(artifact.data, null, 2).length > 100
                              ? `${Object.keys(artifact.data).length} Eigenschaften`
                              : JSON.stringify(artifact.data)}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          Erstellt: {new Date(artifact.createdAt).toLocaleDateString('de-DE')}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEditArtifact(artifact)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Bearbeiten"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteArtifact(artifact.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Löschen"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {createModalOpen && (
        <CreateArtifactModal
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreateArtifact}
        />
      )}

      {editModalOpen && selectedArtifact && (
        <EditArtifactModal
          artifact={selectedArtifact}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedArtifact(null);
          }}
          onSubmit={(updateData) => handleUpdateArtifact(selectedArtifact.id, updateData)}
        />
      )}
    </div>
  );
};